import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    Box,
    Checkbox,
    Chip,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { Dayjs } from 'dayjs';
import isEqual from 'lodash.isequal';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CustomerUserDetails, MalfunctionTypeEnum } from '../../../backendsdk';
import { TrackedMenuItem as MenuItem } from '../../../components/TrackedComponents';
import useProfile from '../../../hooks/profile';
import { localeSort } from '../../../utils/Str';
import { RELEASED_MALFUNCTIONS } from '../Malfunctions/MalfunctionsComponent';
import { OE_ROLES } from '../Users/UsersComponent';
import { ReportRuleFilterChips } from './ReportRuleFilterChips';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export interface BaseAlertReport {
    pk: number;
    report_type: string;
    filters: Record<string, Array<string> | string>;
    is_report: boolean;
    active: boolean;
}

export interface Alert extends Omit<BaseAlertReport, 'is_report'> {
    is_report: false;
    cooldown_minutes: number;
    active_start_time: Dayjs | string | null;
    active_end_time: Dayjs | string | null;
    method: 'EMAIL' | 'SMS';
}

export interface Report extends Omit<BaseAlertReport, 'is_report'> {
    report_scope: 'DAILY' | 'WEEKLY' | 'MONTHLY';
    report_day: number;
    report_time: string;
    is_report: true;
}

export type AlertReport = Alert | Report;

export const SCHEDULING_OPTIONS = {
    REPORT: 'REPORT',
    ALERT: 'ALERT',
};

/** Stored in report filters JSON; matches backend `CoachingSessionsReport`. */
export const SESSION_STATUS_FILTER_KEY = 'SESSION_STATUS';
export const SESSION_STATUS_VALUES = ['PENDING', 'COMPLETED'] as const;

export const EVENT_FILTER_OPTIONS = {
    EYES_OFF_ROAD: 'EYES_OFF_ROAD',
    SLEEP: 'SLEEP',
    TAILGATE: 'TAILGATE',
    DRIVER_EVENT: 'DRIVER_EVENT',
    HARD_BRAKE: 'HARD_BRAKE',
    CRASH_INCIDENT: 'CRASH_INCIDENT',
    COLLISION_TRACK: 'COLLISION_TRACK',
    NEAR_COLLISION: 'NEAR_COLLISION',
    SPEEDING: 'SPEEDING',
    IN_CABIN_CAMERA_COVERED: 'IN_CABIN_CAMERA_COVERED',
    RECURRING_TG: 'RECURRING_TG',
    RECURRING_EOR: 'RECURRING_EOR',
    CELLPHONE: 'CELLPHONE',
};

export const SEVERITY_FILTER_OPTIONS = {
    ALL: 'ALL',
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    CUSTOM: 'CUSTOM',
};

export const SEVERITY_FILTER_VALUES = {
    LOW: '25',
    MEDIUM: '50',
    HIGH: '75',
};

export const REVERSED_SEVERITY_FILTER_VALUES = Object.entries(SEVERITY_FILTER_VALUES).reduce(
    (acc, [key, value]) => ({ ...acc, [value]: key }),
    {} as Record<string, string>,
);

interface SafetyEventFormProps {
    details: AlertReport;
    handleChange: CallableFunction;
    inDialog?: boolean;
}

export const SafetyEventForm: React.FC<SafetyEventFormProps> = (props: SafetyEventFormProps) => {
    const { t } = useTranslation();

    const setSeverityValue = (value: string) => {
        const newFilter = { ...props.details.filters };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        newFilter['SEVERITY'] = [SEVERITY_FILTER_OPTIONS.CUSTOM, value];
        props.handleChange('filters', newFilter);
    };

    const eventTypesStyles = props.inDialog
        ? { mb: 1.5 }
        : {
              mr: 1,
              mt: 1.5,
              '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
              '& .MuiInputBase-input': {
                  minWidth: '0px !important',
                  paddingLeft: !!props.details.filters['EVENT_TYPE']?.length ? '0px !important' : '6px',
                  paddingRight: !!props.details.filters['EVENT_TYPE']?.length ? '0px !important' : '4px',
              },
              minWidth: '250px',
              flexShrink: 0,
          };
    const severityStyles = props.inDialog ? { mb: 2 } : { width: '200px', flexShrink: 0, mr: 1, mt: 1.5 };
    const customSeverityStyles = props.inDialog ? { ml: 1 } : { width: '100px', flexShrink: 0, mt: 1.5 };

    const severityFields = (
        <>
            <FormControl fullWidth sx={severityStyles}>
                <InputLabel id="severity-dropdown-label">{t('content.reports.form.filter.severity_header')}</InputLabel>
                <Select
                    id="severity-dropdown"
                    data-testid="severity-dropdown"
                    labelId="severity-dropdown-label"
                    fullWidth
                    size="small"
                    label={t('content.reports.form.filter.severity_header')}
                    defaultValue="ALL"
                    value={
                        props.details.filters['SEVERITY']
                            ? props.details.filters['SEVERITY'][0]
                            : SEVERITY_FILTER_OPTIONS.ALL
                    }
                    onChange={(e: SelectChangeEvent) => {
                        const newFilter = { ...props.details.filters };
                        const value = e.target.value;
                        if (value == SEVERITY_FILTER_OPTIONS.ALL) {
                            delete newFilter['SEVERITY'];
                        } else {
                            newFilter['SEVERITY'] = [value, '0'];
                        }
                        props.handleChange('filters', newFilter);
                    }}
                >
                    {Object.keys(SEVERITY_FILTER_OPTIONS).map((option) => (
                        <MenuItem key={option} value={option} id={option}>
                            {t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {props.details.filters.SEVERITY?.includes(SEVERITY_FILTER_OPTIONS.CUSTOM) ? (
                <TextField
                    fullWidth
                    id="risk-value-field"
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    label={t('content.reports.form.filter.value')}
                    value={props.details.filters['SEVERITY'][1]}
                    onChange={(e) => setSeverityValue(e.target.value)}
                    onBlur={(e) => {
                        if (e.target.value === '') {
                            e.target.value = '';
                        } else if (parseInt(e.target.value) < 0) {
                            setSeverityValue('0');
                        } else if (parseInt(e.target.value) > 100) {
                            setSeverityValue('100');
                        }
                    }}
                    sx={{ customSeverityStyles }}
                />
            ) : null}
        </>
    );

    return (
        <>
            <Autocomplete
                id="event-types-autocomplete"
                data-testid="event-types-autocomplete"
                multiple
                disableCloseOnSelect
                size="small"
                options={Object.keys(EVENT_FILTER_OPTIONS).sort((a, b) => {
                    const translate = (key: string) => t(`content.reports.form.filter.${key.toLocaleLowerCase()}`);
                    return translate(a).localeCompare(translate(b));
                })}
                getOptionLabel={(option) => t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ padding: 0 }}>
                        <Checkbox
                            id={`${option}-checkbox`}
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={selected}
                            size="small"
                        />
                        <Typography sx={{ pt: 0.5 }}>
                            {t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                        </Typography>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField {...params} label={t('content.reports.form.filter.event_header')} />
                )}
                value={(props.details.filters['EVENT_TYPE'] as string[]) || []}
                onChange={(_event, value) => {
                    const newFilter = { ...props.details.filters };
                    newFilter['EVENT_TYPE'] = value;
                    props.handleChange('filters', newFilter);
                }}
                renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                            <Chip
                                size="small"
                                label={
                                    <Typography fontSize={12}>
                                        {t(`content.reports.form.filter.${value[0].toLocaleLowerCase()}`)}
                                    </Typography>
                                }
                                {...getTagProps({ index: 0 })}
                                sx={{ borderRadius: 1 }}
                                onDelete={undefined}
                            />
                            {value.length > 1 ? (
                                <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                            ) : null}
                        </Box>
                    );
                }}
                sx={eventTypesStyles}
            />
            {props.inDialog ? <Box sx={{ width: '100%', display: 'flex' }}>{severityFields}</Box> : severityFields}
        </>
    );
};

const GEOFENCE_TRIGGER_OPTIONS = {
    GEOFENCE_ENTER: 'GEOFENCE_ENTER',
    GEOFENCE_LEAVE: 'GEOFENCE_LEAVE',
};

interface GeofenceEventFormProps extends SafetyEventFormProps {
    geofences: Record<string, string>;
    inDialog?: boolean;
}

export const GeofenceEventForm: React.FC<GeofenceEventFormProps> = (props: GeofenceEventFormProps) => {
    const { t } = useTranslation();

    return (
        <>
            <Autocomplete
                id="geofence-autocomplete"
                multiple
                disableCloseOnSelect
                size="small"
                options={Object.keys(props.geofences).sort(localeSort)}
                getOptionLabel={(option) => props.geofences[option]}
                renderOption={(itemProps, option, { selected }) => (
                    <li {...itemProps} style={{ padding: 0 }}>
                        <Checkbox
                            id={`${option}-checkbox`}
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={selected}
                            size="small"
                        />
                        <Typography sx={{ pt: 0.5 }}>{props.geofences[option]}</Typography>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField {...params} label={t('content.reports.form.filter.geofence_header')} />
                )}
                value={(props.details.filters['GEOFENCE'] as string[]) || []}
                onChange={(_event, value) => {
                    const newFilter = { ...props.details.filters };
                    newFilter['GEOFENCE'] = value;
                    props.handleChange('filters', newFilter);
                }}
                renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                            <Chip
                                size="small"
                                label={<Typography fontSize={12}>{props.geofences[value[0]]}</Typography>}
                                {...getTagProps({ index: 0 })}
                                sx={{ borderRadius: 1 }}
                                onDelete={undefined}
                            />
                            {value.length > 1 ? (
                                <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                            ) : null}
                        </Box>
                    );
                }}
                sx={{
                    minWidth: '200px',
                    flexShrink: 0,
                    '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                    '& .MuiInputBase-input': {
                        minWidth: '0px !important',
                        paddingLeft: !!props.details.filters['GEOFENCE']?.length ? '0px !important' : '6px',
                        paddingRight: !!props.details.filters['GEOFENCE']?.length ? '0px !important' : '4px',
                    },
                    mr: props.inDialog ? 0 : 1,
                    mt: props.inDialog ? 0 : 1.5,
                }}
            />
            <Autocomplete
                id="trigger-autocomplete"
                multiple
                disableCloseOnSelect
                size="small"
                options={Object.keys(GEOFENCE_TRIGGER_OPTIONS).sort((a, b) => {
                    const translate = (key: string) => t(`content.reports.form.filter.${key.toLocaleLowerCase()}`);
                    return translate(a).localeCompare(translate(b));
                })}
                getOptionLabel={(option) => t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ padding: 0 }}>
                        <Checkbox
                            id={`${option.toLocaleLowerCase()}-checkbox`}
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={selected}
                            size="small"
                        />
                        <Typography sx={{ pt: 0.5 }}>
                            {t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                        </Typography>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField {...params} label={t('content.reports.form.filter.trigger_header')} />
                )}
                value={(props.details.filters['TRIGGER'] as string[]) || []}
                onChange={(_event, value) => {
                    const newFilter = { ...props.details.filters };
                    newFilter['TRIGGER'] = value;
                    props.handleChange('filters', newFilter);
                }}
                renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                            <Chip
                                size="small"
                                label={
                                    <Typography fontSize={12}>
                                        {t(`content.reports.form.filter.${value[0].toLocaleLowerCase()}`)}
                                    </Typography>
                                }
                                {...getTagProps({ index: 0 })}
                                sx={{ borderRadius: 1 }}
                                onDelete={undefined}
                            />
                            {value.length > 1 ? (
                                <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                            ) : null}
                        </Box>
                    );
                }}
                sx={{
                    minWidth: '200px',
                    flexShrink: 0,
                    mb: props.inDialog ? 2 : 0,
                    mt: props.inDialog ? 2 : 1.5,
                    mr: props.inDialog ? 0 : 1,
                }}
            />
        </>
    );
};

const IN_CABIN_WELLNESS_OPTIONS = {
    NO_FACE: 'NO_FACE',
    BLOCKED: 'BLOCKED',
    FAULTY: 'FAULTY',
    PERIPHERAL_FACE: 'PERIPHERAL_FACE',
    DISCONNECTED: 'DISCONNECTED',
};

const FRONT_FACING_WELLNESS_OPTIONS = {
    UNWELL_HORIZONTAL: 'UNWELL_HORIZONTAL',
    CAMERA_VERTICAL_HIGH: 'CAMERA_VERTICAL_HIGH',
    CAMERA_VERTICAL_LOW: 'CAMERA_VERTICAL_LOW',
    LOW_QUALITY_INPUT: 'LOW_QUALITY_INPUT',
    INSUFFICIENT_VALID_INPUT: 'INSUFFICIENT_VALID_INPUT',
};

export const WELLNESS_OPTIONS = { ...IN_CABIN_WELLNESS_OPTIONS, ...FRONT_FACING_WELLNESS_OPTIONS };

export const ASSIGNEE_OPTIONS = {
    ALL: 'ALL',
    ASSIGNED_TO_ME: 'ASSIGNED_TO_ME',
    CUSTOMER: 'CUSTOMER',
    OPENEYES: 'OPENEYES',
};

export const ACTION_OPTIONS = {
    NEW: 'NEW',
    FIELD_CHANGE: 'FIELD_CHANGE',
    NEW_COMMENT: 'NEW_COMMENT',
};

export const FIELDS_OPTIONS = {
    STAGE: 'STAGE',
    ASSIGNEE: 'ASSIGNEE',
};

export const DeviceWellnessForm: React.FC<SafetyEventFormProps> = (props: SafetyEventFormProps) => {
    const { t } = useTranslation();

    return (
        <Autocomplete
            fullWidth={props.inDialog}
            id="wellness-types-autocomplete"
            multiple
            disableCloseOnSelect
            size="small"
            options={Object.keys(WELLNESS_OPTIONS).sort((a, b) => {
                const translate = (key: string) => t(`content.reports.form.filter.${key.toLocaleLowerCase()}`);
                return translate(a).localeCompare(translate(b));
            })}
            getOptionLabel={(option) => t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
            renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: 0 }}>
                    <Checkbox
                        id={`${option}-checkbox`}
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                        size="small"
                    />
                    <Typography sx={{ pt: 0.5 }}>
                        {t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                    </Typography>
                </li>
            )}
            renderInput={(params) => <TextField {...params} label={t('content.reports.form.filter.wellness_header')} />}
            value={(props.details.filters['WELLNESS_TYPE'] as string[]) || []}
            onChange={(_event, value) => {
                const newFilter = { ...props.details.filters };
                newFilter['WELLNESS_TYPE'] = value;
                props.handleChange('filters', newFilter);
            }}
            renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                        <Chip
                            size="small"
                            label={
                                <Typography fontSize={12}>
                                    {t(`content.reports.form.filter.${value[0].toLocaleLowerCase()}`)}
                                </Typography>
                            }
                            {...getTagProps({ index: 0 })}
                            sx={{ borderRadius: 1 }}
                            onDelete={undefined}
                        />
                        {value.length > 1 ? <Typography variant="body2">{`+${value.length - 1}`}</Typography> : null}
                    </Box>
                );
            }}
            sx={{
                flexShrink: 0,
                minWidth: '200px',
                mb: props.inDialog ? 1.5 : 0,
                mt: props.inDialog ? 0 : 1.5,
                mr: props.inDialog ? 0 : 1,
                '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                '& .MuiInputBase-input': {
                    minWidth: '0px !important',
                    paddingLeft: !!props.details.filters['ACTION']?.length ? '0px !important' : '6px',
                    paddingRight: !!props.details.filters['ACTION']?.length ? '0px !important' : '4px',
                },
            }}
        />
    );
};

export const AccidentReportForm: React.FC<SafetyEventFormProps> = (props: SafetyEventFormProps) => {
    const { profile } = useProfile();
    const { t } = useTranslation();

    return (
        <>
            <FormControl
                size="small"
                fullWidth={props.inDialog}
                sx={{
                    minWidth: '200px',
                    mb: props.inDialog ? 1.5 : 0,
                    mt: props.inDialog ? 0 : 1.5,
                    mr: props.inDialog ? 0 : 1,
                }}
            >
                <InputLabel>{t('content.reports.form.filter.assignee')}</InputLabel>
                <Select
                    id="assignee-dropdown"
                    fullWidth={props.inDialog}
                    size="small"
                    label={t('content.reports.form.filter.assignee')}
                    defaultValue="ALL"
                    value={props.details.filters['ASSIGNEE'] ? props.details.filters['ASSIGNEE'][0] : ''}
                    onChange={(e: SelectChangeEvent) => {
                        const newFilter = { ...props.details.filters };
                        const value = e.target.value;
                        if (value == ASSIGNEE_OPTIONS.ALL) {
                            delete newFilter['ASSIGNEE'];
                        } else {
                            newFilter['ASSIGNEE'] = [e.target.value];
                        }
                        props.handleChange('filters', newFilter);
                    }}
                >
                    {Object.keys(ASSIGNEE_OPTIONS).map((option) => (
                        <MenuItem key={option} value={option} id={option}>
                            {option === ASSIGNEE_OPTIONS.CUSTOMER
                                ? profile.customer.name
                                : t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Autocomplete
                fullWidth={props.inDialog}
                id="accident-action-autocomplete"
                multiple
                disableCloseOnSelect
                size="small"
                options={Object.keys(ACTION_OPTIONS).sort((a, b) => {
                    const translate = (key: string) => t(`content.reports.form.filter.${key.toLocaleLowerCase()}`);
                    return translate(a).localeCompare(translate(b));
                })}
                getOptionLabel={(option) => t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ padding: 0 }}>
                        <Checkbox
                            id={`${option}-checkbox`}
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={selected}
                            size="small"
                        />
                        <Typography sx={{ pt: 0.5 }}>
                            {t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                        </Typography>
                    </li>
                )}
                renderInput={(params) => <TextField {...params} label={t('content.reports.form.filter.action')} />}
                value={(props.details.filters['ACTION'] as string[]) || []}
                onChange={(_event, value) => {
                    const newFilter = { ...props.details.filters };
                    newFilter['ACTION'] = value;
                    props.handleChange('filters', newFilter);
                }}
                renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                            <Chip
                                size="small"
                                label={
                                    <Typography fontSize={12}>
                                        {t(`content.reports.form.filter.${value[0].toLocaleLowerCase()}`)}
                                    </Typography>
                                }
                                {...getTagProps({ index: 0 })}
                                sx={{ borderRadius: 1 }}
                                onDelete={undefined}
                            />
                            {value.length > 1 ? (
                                <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                            ) : null}
                        </Box>
                    );
                }}
                sx={{
                    flexShrink: 0,
                    minWidth: '200px',
                    mb: props.inDialog ? 1.5 : 0,
                    mt: props.inDialog ? 0 : 1.5,
                    '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                    '& .MuiInputBase-input': {
                        minWidth: '0px !important',
                        paddingLeft: !!props.details.filters['ACTION']?.length ? '0px !important' : '6px',
                        paddingRight: !!props.details.filters['ACTION']?.length ? '0px !important' : '4px',
                    },
                }}
            />
            <Autocomplete
                fullWidth={props.inDialog}
                id="accident-field-autocomplete"
                multiple
                disableCloseOnSelect
                size="small"
                options={Object.keys(FIELDS_OPTIONS).sort((a, b) => {
                    const translate = (key: string) => t(`content.reports.form.filter.${key.toLocaleLowerCase()}`);
                    return translate(a).localeCompare(translate(b));
                })}
                getOptionLabel={(option) => t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ padding: 0 }}>
                        <Checkbox
                            id={`${option}-checkbox`}
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={selected}
                            size="small"
                        />
                        <Typography sx={{ pt: 0.5 }}>
                            {t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                        </Typography>
                    </li>
                )}
                renderInput={(params) => <TextField {...params} label={t('content.reports.form.filter.fields')} />}
                value={(props.details.filters['FIELDS'] as string[]) || []}
                onChange={(_event, value) => {
                    const newFilter = { ...props.details.filters };
                    newFilter['FIELDS'] = value;
                    props.handleChange('filters', newFilter);
                }}
                renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                            <Chip
                                size="small"
                                label={
                                    <Typography fontSize={12}>
                                        {t(`content.reports.form.filter.${value[0].toLocaleLowerCase()}`)}
                                    </Typography>
                                }
                                {...getTagProps({ index: 0 })}
                                sx={{ borderRadius: 1 }}
                                onDelete={undefined}
                            />
                            {value.length > 1 ? (
                                <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                            ) : null}
                        </Box>
                    );
                }}
                sx={{
                    flexShrink: 0,
                    minWidth: '200px',
                    mb: props.inDialog ? 1.5 : 0,
                    mt: props.inDialog ? 0 : 1.5,
                    mx: props.inDialog ? 0 : 1,
                    '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                    '& .MuiInputBase-input': {
                        minWidth: '0px !important',
                        paddingLeft: !!props.details.filters['FIELDS']?.length ? '0px !important' : '6px',
                        paddingRight: !!props.details.filters['FIELDS']?.length ? '0px !important' : '4px',
                    },
                }}
            />
        </>
    );
};

export const MalfunctionsReportForm: React.FC<SafetyEventFormProps> = (props: SafetyEventFormProps) => {
    const { t } = useTranslation();
    const { profile } = useProfile();
    const isInCabinTask = isEqual(props.details.filters['TYPE'], ['IN_CABIN_V1']);
    const isFrontFacingTask = isEqual(props.details.filters['TYPE'], ['FRONT_FACING']);

    return (
        <>
            <Autocomplete
                fullWidth={props.inDialog}
                id="malfunction-types-autocomplete"
                multiple
                disableCloseOnSelect
                size="small"
                options={Object.values(MalfunctionTypeEnum)
                    .filter((type) => RELEASED_MALFUNCTIONS.includes(type) || OE_ROLES.includes(profile.role))
                    .sort((a, b) => {
                        const translate = (key: string) =>
                            t(`content.malfunctions.table.type.${key.toLocaleLowerCase()}`);
                        return translate(a).localeCompare(translate(b));
                    })}
                getOptionLabel={(option) => t(`content.malfunctions.table.type.${option.toLocaleLowerCase()}`)}
                renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ padding: 0 }}>
                        <Checkbox
                            id={`${option}-checkbox`}
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={selected}
                            size="small"
                        />
                        <Typography sx={{ pt: 0.5 }}>
                            {t(`content.malfunctions.table.type.${option.toLocaleLowerCase()}`)}
                        </Typography>
                    </li>
                )}
                renderInput={(params) => <TextField {...params} label={t('content.malfunctions.table.malfunction')} />}
                value={(props.details.filters['TYPE'] as string[]) || []}
                onChange={(_event, value) => {
                    const newFilter = { ...props.details.filters };
                    newFilter['TYPE'] = value;
                    if (!isEqual(newFilter['TYPE'], ['IN_CABIN_V1']) && !isEqual(newFilter['TYPE'], ['FRONT_FACING'])) {
                        delete newFilter['WELLNESS_TYPE'];
                    }
                    props.handleChange('filters', newFilter);
                }}
                renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                            <Chip
                                size="small"
                                label={
                                    <Typography fontSize={12}>
                                        {t(`content.malfunctions.table.type.${value[0].toLocaleLowerCase()}`)}
                                    </Typography>
                                }
                                {...getTagProps({ index: 0 })}
                                sx={{ borderRadius: 1 }}
                                onDelete={undefined}
                            />
                            {value.length > 1 ? (
                                <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                            ) : null}
                        </Box>
                    );
                }}
                sx={{
                    flexShrink: 0,
                    minWidth: '200px',
                    mb: props.inDialog ? 1.5 : 0,
                    mt: props.inDialog ? 0 : 1.5,
                    '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                    '& .MuiInputBase-input': {
                        minWidth: '0px !important',
                        paddingLeft: !!props.details.filters['ACTION']?.length ? '0px !important' : '6px',
                        paddingRight: !!props.details.filters['ACTION']?.length ? '0px !important' : '4px',
                    },
                    mr: props.inDialog ? 0 : 1,
                }}
            />
            <Autocomplete
                fullWidth={props.inDialog}
                id="wellness-types-autocomplete"
                multiple
                disableCloseOnSelect
                size="small"
                options={Object.keys(isInCabinTask ? IN_CABIN_WELLNESS_OPTIONS : FRONT_FACING_WELLNESS_OPTIONS).sort(
                    (a, b) => {
                        const translate = (key: string) => t(`content.reports.form.filter.${key.toLocaleLowerCase()}`);
                        return translate(a).localeCompare(translate(b));
                    },
                )}
                getOptionLabel={(option) => t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ padding: 0 }}>
                        <Checkbox
                            id={`${option}-checkbox`}
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={selected}
                            size="small"
                        />
                        <Typography sx={{ pt: 0.5 }}>
                            {t(`content.reports.form.filter.${option.toLocaleLowerCase()}`)}
                        </Typography>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField {...params} label={t('content.reports.form.filter.wellness_header')} />
                )}
                disabled={!isInCabinTask && !isFrontFacingTask}
                value={(props.details.filters['WELLNESS_TYPE'] as string[]) || []}
                onChange={(_event, value) => {
                    const newFilter = { ...props.details.filters };
                    newFilter['WELLNESS_TYPE'] = value;
                    props.handleChange('filters', newFilter);
                }}
                renderTags={(value: Array<string>, getTagProps: AutocompleteRenderGetTagProps) => {
                    return (
                        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                            <Chip
                                size="small"
                                label={
                                    <Typography fontSize={12}>
                                        {t(`content.reports.form.filter.${value[0].toLocaleLowerCase()}`)}
                                    </Typography>
                                }
                                {...getTagProps({ index: 0 })}
                                sx={{ borderRadius: 1 }}
                                onDelete={undefined}
                            />
                            {value.length > 1 ? (
                                <Typography variant="body2">{`+${value.length - 1}`}</Typography>
                            ) : null}
                        </Box>
                    );
                }}
                sx={{
                    flexShrink: 0,
                    minWidth: '200px',
                    mb: props.inDialog ? 1.5 : 0,
                    mt: props.inDialog ? 0 : 1.5,
                    mr: props.inDialog ? 0 : 1,
                    '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                    '& .MuiInputBase-input': {
                        minWidth: '0px !important',
                        paddingLeft: !!props.details.filters['ACTION']?.length ? '0px !important' : '6px',
                        paddingRight: !!props.details.filters['ACTION']?.length ? '0px !important' : '4px',
                    },
                }}
            />
        </>
    );
};

export const CoachingSessionsStatusForm: React.FC<SafetyEventFormProps> = (props) => {
    const { t } = useTranslation();
    return (
        <Autocomplete
            fullWidth={props.inDialog}
            id="coaching-session-status-filter"
            data-testid="coaching-session-status-filter"
            multiple
            disableCloseOnSelect
            size="small"
            options={[...SESSION_STATUS_VALUES]}
            getOptionLabel={(option) => t(`content.reports.form.filter.session_status_${option.toLocaleLowerCase()}`)}
            renderOption={(liProps, option, { selected }) => (
                <li {...liProps} style={{ padding: 0 }}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} size="small" />
                    <Typography sx={{ pt: 0.5 }}>
                        {t(`content.reports.form.filter.session_status_${option.toLocaleLowerCase()}`)}
                    </Typography>
                </li>
            )}
            renderInput={(params) => (
                <TextField {...params} label={t('content.reports.form.filter.session_status_header')} />
            )}
            value={
                (props.details.filters[SESSION_STATUS_FILTER_KEY] as string[] | undefined) ?? [...SESSION_STATUS_VALUES]
            }
            onChange={(_event, value) => {
                const newFilter = { ...props.details.filters };
                newFilter[SESSION_STATUS_FILTER_KEY] = value;
                props.handleChange('filters', newFilter);
            }}
            sx={{
                minWidth: '350px',
                flexShrink: 0,
                mb: props.inDialog ? 1.5 : 0,
                mt: props.inDialog ? 0 : 1.5,
                mr: props.inDialog ? 0 : 1,
            }}
        />
    );
};

interface GetClipCompletionAlertFormProps {
    details: AlertReport;
    handleChange: CallableFunction;
    users: CustomerUserDetails[];
    inDialog?: boolean;
}

export const GetClipCompletionAlertForm: React.FC<GetClipCompletionAlertFormProps> = (props) => {
    const { t } = useTranslation();
    const selectedIds = (props.details.filters['REQUESTING_USER'] as string[] | undefined) || [];
    const value = props.users.filter((u) => selectedIds.includes(String(u.user_id)));
    return (
        <Autocomplete
            id="get-clip-requesting-users-autocomplete"
            data-testid="get-clip-requesting-users-autocomplete"
            multiple
            disableCloseOnSelect
            size="small"
            options={props.users}
            getOptionLabel={(u) => u.name}
            isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
            value={value}
            onChange={(_e, newValue) => {
                const newFilter = { ...props.details.filters };
                newFilter['REQUESTING_USER'] = newValue.map((u) => String(u.user_id));
                props.handleChange('filters', newFilter);
            }}
            renderOption={(optionProps, option, { selected }) => (
                <li {...optionProps} style={{ padding: 0 }}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} size="small" />
                    <Typography sx={{ pt: 0.5 }}>{option.name}</Typography>
                </li>
            )}
            renderInput={(params) => (
                <TextField {...params} label={t('content.reports.form.filter.requesting_users')} />
            )}
            renderTags={(tagValue: CustomerUserDetails[]) => {
                const values = tagValue.sort((a, b) => a.name.localeCompare(b.name)).map((u) => u.name);
                return <ReportRuleFilterChips filterKey="requesting_user" values={values} maxChips={2} />;
            }}
            sx={{
                flexShrink: 0,
                minWidth: '360px',
                mb: props.inDialog ? 1.5 : 0,
                mt: props.inDialog ? 0 : 1.5,
                mr: props.inDialog ? 0 : 1,
                '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
                '& .MuiInputBase-input': {
                    minWidth: '0px !important',
                    paddingLeft: value.length > 0 ? '0px !important' : '6px',
                    paddingRight: value.length > 0 ? '0px !important' : '4px',
                },
            }}
        />
    );
};
